import { isInteractive } from '../../internal/components/option/utils/filter-options';
import { useEffect, useRef } from 'react';
import { createHighlightedOptionHook } from '../../internal/components/options-list/utils/use-highlight-option';
import { useOpenState } from '../../internal/components/options-list/utils/use-open-state';
import { useMenuKeyboard, useTriggerKeyboard } from '../../internal/components/options-list/utils/use-keyboard';
import { useIds } from '../../internal/components/options-list/utils/use-ids';
import { connectOptionsByValue } from './connect-options';
import useForwardFocus from '../../internal/hooks/forward-focus';
import { usePrevious } from '../../internal/hooks/use-previous';
var useHighlightedOption = createHighlightedOptionHook({ isInteractive: isInteractive });
export function useSelect(_a) {
    var selectedOptions = _a.selectedOptions, updateSelectedOption = _a.updateSelectedOption, options = _a.options, filteringType = _a.filteringType, rootRef = _a.rootRef, externalRef = _a.externalRef, keepOpen = _a.keepOpen, fireLoadItems = _a.fireLoadItems, setFilteringValue = _a.setFilteringValue;
    var filterRef = useRef(null);
    var triggerRef = useRef(null);
    var menuRef = useRef(null);
    var hasFilter = filteringType !== 'none';
    var activeRef = hasFilter ? filterRef : menuRef;
    var isKeyboard = useRef(false);
    var isSelectingUsingSpace = useRef(false);
    var __selectedOptions = connectOptionsByValue(options, selectedOptions);
    var _b = useHighlightedOption(options), highlightedOption = _b.highlightedOption, highlightedIndex = _b.highlightedIndex, moveHighlight = _b.moveHighlight, resetHighlight = _b.resetHighlight, setHighlightedIndex = _b.setHighlightedIndex, highlightOption = _b.highlightOption, goHome = _b.goHome, goEnd = _b.goEnd;
    var _c = useOpenState({
        onOpen: function () { return fireLoadItems(''); },
        onClose: function () {
            resetHighlight();
            setFilteringValue('');
        }
    }), isOpen = _c.isOpen, openDropdown = _c.openDropdown, closeDropdown = _c.closeDropdown, toggleDropdown = _c.toggleDropdown;
    var hasHighlightedOption = !!highlightedOption;
    var hasSelectedOption = __selectedOptions.length > 0;
    var _d = useIds({ hasHighlightedOption: hasHighlightedOption, hasSelectedOption: hasSelectedOption }), highlightedOptionId = _d.highlightedOptionId, selectedOptionId = _d.selectedOptionId, menuId = _d.menuId;
    var selectOption = function (option) {
        isSelectingUsingSpace.current = false;
        var optionToSelect = option || highlightedOption;
        if (!optionToSelect) {
            return;
        }
        updateSelectedOption(optionToSelect.option);
        !keepOpen && closeDropdown();
    };
    var activeKeyDownHandler = useMenuKeyboard({
        moveHighlight: moveHighlight,
        selectOption: selectOption,
        goHome: goHome,
        goEnd: goEnd,
        closeDropdown: closeDropdown,
        isKeyboard: isKeyboard,
        isSelectingUsingSpace: isSelectingUsingSpace,
        preventNativeSpace: !hasFilter
    });
    var triggerKeyDownHandler = useTriggerKeyboard({ openDropdown: openDropdown, goHome: goHome, isKeyboard: isKeyboard });
    var getTriggerProps = function (disabled) {
        if (disabled === void 0) { disabled = false; }
        var triggerProps = {
            ref: triggerRef
        };
        if (!disabled) {
            triggerProps.onMouseDown = function (event) {
                event.preventDefault(); // prevent current focus from blurring as it immediately closes the dropdown
                toggleDropdown();
            };
            triggerProps.onKeyDown = triggerKeyDownHandler;
        }
        if (hasSelectedOption) {
            triggerProps.ariaLabelledby = selectedOptionId;
        }
        return triggerProps;
    };
    var getFilterProps = function () {
        var _a;
        if (!hasFilter) {
            return {};
        }
        return {
            ref: filterRef,
            onKeyDown: activeKeyDownHandler,
            onBlur: closeDropdown,
            onChange: function (event) {
                setFilteringValue(event.detail.value);
                resetHighlight();
            },
            __onDelayedInput: function (event) {
                fireLoadItems(event.detail.value);
            },
            __nativeAttributes: (_a = {
                    'aria-activedescendant': highlightedOptionId
                },
                _a['aria-owns'] = menuId,
                _a)
        };
    };
    var getMenuProps = function () {
        var menuProps = {
            id: menuId,
            ref: menuRef,
            open: isOpen,
            onMouseUp: function (itemIndex) {
                isKeyboard.current = false;
                if (itemIndex > -1) {
                    selectOption(options[itemIndex]);
                }
            },
            onMouseMove: function (itemIndex) {
                isKeyboard.current = false;
                if (itemIndex > -1) {
                    setHighlightedIndex(itemIndex);
                }
            }
        };
        if (!hasFilter) {
            menuProps.onKeyDown = activeKeyDownHandler;
            menuProps.onBlur = closeDropdown;
            menuProps.nativeAttributes = {
                'aria-activedescendant': highlightedOptionId
            };
        }
        return menuProps;
    };
    var getOptionProps = function (option, index) {
        var _a;
        var highlighted = option === highlightedOption;
        var selected = __selectedOptions.indexOf(option) > -1;
        var isNextSelected = __selectedOptions.indexOf(options[index + 1]) > -1;
        var optionProps = (_a = {
                key: index,
                option: option,
                highlighted: highlighted,
                selected: selected,
                isNextSelected: isNextSelected
            },
            _a['data-mouse-target'] = isInteractive(option) ? index : -1,
            _a);
        if (highlighted) {
            optionProps.id = highlightedOptionId;
        }
        return optionProps;
    };
    var prevOpen = usePrevious(isOpen);
    useEffect(function () {
        // highlight the first selected option, when opening the Select component
        if (isOpen && !prevOpen && hasSelectedOption) {
            setHighlightedIndex(options.indexOf(__selectedOptions[0]));
        }
    }, [isOpen, __selectedOptions, hasSelectedOption, setHighlightedIndex, options, prevOpen]);
    useEffect(function () {
        var _a, _b, _c;
        if (isOpen) {
            // dropdown-fit calculations ensure that the dropdown will fit inside the current
            // viewport, so prevent the browser from trying to scroll it into view (e.g. if
            // scroll-padding-top is set on a parent)
            (_a = activeRef.current) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true });
        }
        else if ((_b = rootRef.current) === null || _b === void 0 ? void 0 : _b.contains(document.activeElement)) {
            (_c = triggerRef.current) === null || _c === void 0 ? void 0 : _c.focus();
        }
    }, [isOpen, rootRef, activeRef, triggerRef]);
    useForwardFocus(externalRef, triggerRef);
    var announceSelected = !!highlightedOption && __selectedOptions.indexOf(highlightedOption) > -1;
    return {
        isOpen: isOpen,
        highlightedOption: highlightedOption,
        highlightedIndex: highlightedIndex,
        getTriggerProps: getTriggerProps,
        getMenuProps: getMenuProps,
        getFilterProps: getFilterProps,
        getOptionProps: getOptionProps,
        isKeyboard: isKeyboard,
        highlightOption: highlightOption,
        selectOption: selectOption,
        announceSelected: announceSelected
    };
}
//# sourceMappingURL=use-select.js.map